import React, { forwardRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import ru from "date-fns/locale/ru";
import en from "date-fns/locale/en-US";
import kk from "date-fns/locale/kk";
import zh from "date-fns/locale/zh-CN";
import "./ExpectedArrivalCalendar.scss";
import EditIconNew from "../../../../shared/icons/EditIconNew";

registerLocale("ru", ru);
registerLocale("en", en);
registerLocale("kz", kk);
registerLocale("cn", zh);

const POPPER_MODIFIERS = [
  { name: "offset", options: { offset: [0, 8] } },
  { name: "preventOverflow", options: { padding: 8 } },
  { name: "flip", options: { padding: 8 } },
];

const TriggerButton = forwardRef(({ onClick }, ref) => {
  const { t } = useTranslation();
  return (
    <button ref={ref} type="button" className="ExpectedArrivalCalendar__trigger" onClick={onClick}>
      <EditIconNew />
      <span>{t("common.change")}</span>
    </button>
  );
});
TriggerButton.displayName = "TriggerButton";

const ExpectedArrivalCalendar = ({ selected, onChange, onClear, onSelect, isLoading = false }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const currentLocale = i18n.language || "ru";
  const [open, setOpen] = useState(false);

  const handleSelect = async () => {
    if (onSelect) await onSelect();
    setOpen(false);
  };

  const handleClear = () => {
    if (onClear) onClear();
    setOpen(false);
  };

  return (
    <div className="ExpectedArrivalCalendar">
      <DatePicker
        selected={selected}
        onChange={onChange}
        open={open}
        onInputClick={() => setOpen((prev) => !prev)}
        onClickOutside={() => setOpen(false)}
        shouldCloseOnSelect={false}
        disabledKeyboardNavigation
        locale={currentLocale}
        customInput={<TriggerButton />}
        popperPlacement="bottom-end"
        popperModifiers={POPPER_MODIFIERS}
        popperProps={{ strategy: "fixed" }}
        popperClassName="ExpectedArrivalCalendar__popper"
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="react-datepicker__header">
            <div className="react-datepicker__current-month">
              {date.toLocaleString(currentLocale, { month: "long" })} {date.getFullYear()}
            </div>
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
                Previous Month
              </span>
            </button>
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--next"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
                Next Month
              </span>
            </button>
          </div>
        )}
      >
        <div className="ExpectedArrivalCalendar__footer">
          <button type="button" className="ExpectedArrivalCalendar__clear" onClick={handleClear} disabled={isLoading}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="ExpectedArrivalCalendar__select"
            onClick={handleSelect}
            disabled={isLoading}
          >
            {t("common.save")}
          </button>
        </div>
      </DatePicker>
    </div>
  );
};

export default ExpectedArrivalCalendar;
